import { useEffect, useId } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { ComparisonData } from "@/content/comparison";

function SeoHead({ data }: { data: ComparisonData }) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDescription =
      document
        .querySelector('meta[name="description"]')
        ?.getAttribute("content") ?? null;
    const prevOgTitle =
      document
        .querySelector('meta[property="og:title"]')
        ?.getAttribute("content") ?? null;
    const prevOgDescription =
      document
        .querySelector('meta[property="og:description"]')
        ?.getAttribute("content") ?? null;
    const prevOgUrl =
      document
        .querySelector('meta[property="og:url"]')
        ?.getAttribute("content") ?? null;

    document.title = data.seo.title;
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const canonicalUrl = `https://www.passiveagents.com/vs/${data.competitor.id}`;
    let canonicalEl = document.querySelector<HTMLLinkElement>(
      "link[rel='canonical']",
    );
    const canonicalWasNew = !canonicalEl;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      document.head.appendChild(canonicalEl);
    }
    const prevCanonical = canonicalEl.href;
    canonicalEl.href = canonicalUrl;

    setMeta("description", data.seo.description);
    setOg("og:title", data.seo.title);
    setOg("og:description", data.seo.description);
    setOg("og:url", canonicalUrl);

    return () => {
      document.title = prevTitle;
      if (prevDescription !== null)
        document
          .querySelector('meta[name="description"]')
          ?.setAttribute("content", prevDescription);
      if (prevOgTitle !== null)
        document
          .querySelector('meta[property="og:title"]')
          ?.setAttribute("content", prevOgTitle);
      if (prevOgDescription !== null)
        document
          .querySelector('meta[property="og:description"]')
          ?.setAttribute("content", prevOgDescription);
      if (prevOgUrl !== null)
        document
          .querySelector('meta[property="og:url"]')
          ?.setAttribute("content", prevOgUrl);
      else document.querySelector('meta[property="og:url"]')?.remove();
      const el = document.querySelector<HTMLLinkElement>(
        "link[rel='canonical']",
      );
      if (el) {
        if (canonicalWasNew) el.remove();
        else el.href = prevCanonical;
      }
    };
  }, [data]);

  return null;
}

function FaqSchema({ data }: { data: ComparisonData }) {
  const uid = useId();
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faq.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    };
    const script = document.createElement("script");
    script.id = `faq-schema-${uid}`;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(script.id);
      if (el) el.remove();
    };
  }, [data, uid]);

  return null;
}

function ComparisonTable({ data }: { data: ComparisonData }) {
  return (
    <section className="relative border-t border-border dark:border-indigo-500/20 bg-background dark:bg-[#0c0a2a]">
      <div className="mx-auto max-w-5xl px-6 py-16 md:py-24">
        <h2 className="mb-2 font-mono text-xl font-bold tracking-tight text-foreground dark:text-white md:text-2xl">
          Quick comparison
        </h2>
        <p className="mb-8 text-muted-foreground dark:text-indigo-200/70">
          How PassiveAgents and {data.competitor.name} compare at a glance.
        </p>
        <div className="overflow-x-auto rounded-2xl border border-border bg-card dark:border-indigo-500/20 dark:bg-[#0e0c30]/80 backdrop-blur-sm">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border dark:border-indigo-500/20">
                <th className="px-5 py-4 font-mono font-bold text-foreground dark:text-white">
                  Feature
                </th>
                <th className="px-5 py-4 font-mono font-bold text-yellow-600 dark:text-yellow-300">
                  PassiveAgents
                </th>
                <th className="px-5 py-4 font-mono font-bold text-primary dark:text-indigo-200">
                  {data.competitor.name}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.quickComparisonTable.map((row, i) => (
                <tr
                  key={i}
                  className="border-t border-border/70 dark:border-indigo-500/10"
                >
                  <td className="px-5 py-3 font-medium text-foreground dark:text-indigo-100/80">
                    {row.label}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground dark:text-indigo-100/70">
                    {row.passiveAgents}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground dark:text-indigo-100/70">
                    {row.competitor}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function BestForSection({ data }: { data: ComparisonData }) {
  return (
    <section className="relative border-t border-border dark:border-indigo-500/20 bg-muted/40 dark:bg-[#0a0825]">
      <div className="mx-auto max-w-5xl px-6 py-16 md:py-24">
        <h2 className="mb-2 font-mono text-xl font-bold tracking-tight text-foreground dark:text-white md:text-2xl">
          Best for each product
        </h2>
        <p className="mb-8 text-muted-foreground dark:text-indigo-200/70">
          {data.competitor.name} and PassiveAgents serve different needs. Here
          is when each one makes sense.
        </p>
        <div className="grid gap-6 md:grid-cols-2">
          {data.bestFor.map((item, i) => (
            <div
              key={i}
              className={`rounded-2xl border p-6 backdrop-blur-sm ${
                item.product === "competitor"
                  ? "border-border bg-card dark:border-indigo-500/20 dark:bg-[#0e0c30]/80"
                  : "border-amber-500/30 bg-card dark:border-yellow-400/20 dark:bg-[#0e0c30]/80"
              }`}
            >
              <h3
                className={`mb-4 font-mono text-lg font-bold ${
                  item.product === "competitor"
                    ? "text-primary dark:text-indigo-200"
                    : "text-yellow-600 dark:text-yellow-300"
                }`}
              >
                {item.heading}
              </h3>
              <ul className="space-y-3">
                {item.points.map((point, j) => (
                  <li
                    key={j}
                    className="flex items-start gap-2 text-foreground dark:text-indigo-100/80"
                  >
                    <span
                      className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                        item.product === "competitor"
                          ? "bg-indigo-400"
                          : "bg-yellow-400"
                      }`}
                    />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KeyDifferencesSection({ data }: { data: ComparisonData }) {
  return (
    <section className="relative border-t border-border dark:border-indigo-500/20 bg-background dark:bg-[#0c0a2a]">
      <div className="mx-auto max-w-5xl px-6 py-16 md:py-24">
        <h2 className="mb-2 font-mono text-xl font-bold tracking-tight text-foreground dark:text-white md:text-2xl">
          Key differences
        </h2>
        <p className="mb-8 text-muted-foreground dark:text-indigo-200/70">
          The fundamental ways PassiveAgents and {data.competitor.name} differ.
        </p>
        <div className="space-y-6">
          {data.keyDifferences.map((diff, i) => (
            <div
              key={i}
              className="rounded-2xl border border-border bg-card dark:border-indigo-500/20 dark:bg-[#0e0c30]/80 p-6 backdrop-blur-sm"
            >
              <h3 className="mb-4 font-mono text-base font-bold text-foreground dark:text-white">
                {diff.heading}
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-yellow-400/20 bg-yellow-400/5 p-4">
                  <p className="mb-2 font-mono text-xs font-semibold uppercase tracking-wider text-yellow-600 dark:text-yellow-300">
                    PassiveAgents
                  </p>
                  <p className="text-sm text-foreground dark:text-indigo-100/80">
                    {diff.passiveAgents}
                  </p>
                </div>
                <div className="rounded-xl border border-border dark:border-indigo-500/20 bg-primary/5 dark:bg-indigo-400/5 p-4">
                  <p className="mb-2 font-mono text-xs font-semibold uppercase tracking-wider text-primary dark:text-indigo-200">
                    {data.competitor.name}
                  </p>
                  <p className="text-sm text-foreground dark:text-indigo-100/80">
                    {diff.competitor}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhenToUseSection({ data }: { data: ComparisonData }) {
  return (
    <section className="relative border-t border-border dark:border-indigo-500/20 bg-muted/40 dark:bg-[#0a0825]">
      <div className="mx-auto max-w-5xl px-6 py-16 md:py-24">
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="mb-4 font-mono text-lg font-bold tracking-tight text-primary dark:text-indigo-200 md:text-xl">
              When to use {data.competitor.name}
            </h2>
            <ul className="space-y-3">
              {data.whenToUseCompetitor.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-foreground dark:text-indigo-100/80"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="mb-4 font-mono text-lg font-bold tracking-tight text-yellow-600 dark:text-yellow-300 md:text-xl">
              When to use PassiveAgents
            </h2>
            <ul className="space-y-3">
              {data.whenToUsePassiveAgents.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-foreground dark:text-indigo-100/80"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function CanYouUseBothSection({ data }: { data: ComparisonData }) {
  return (
    <section className="relative border-t border-border dark:border-indigo-500/20 bg-background dark:bg-[#0c0a2a]">
      <div className="mx-auto max-w-3xl px-6 py-16 text-center md:py-20">
        <h2 className="mb-4 font-mono text-xl font-bold tracking-tight text-foreground dark:text-white md:text-2xl">
          Can you use both?
        </h2>
        <p className="text-base leading-relaxed text-foreground dark:text-indigo-100/80">
          {data.canYouUseBoth}
        </p>
      </div>
    </section>
  );
}

function FaqSection({ data }: { data: ComparisonData }) {
  return (
    <section className="relative border-t border-border dark:border-indigo-500/20 bg-muted/40 dark:bg-[#0a0825]">
      <div className="mx-auto max-w-4xl px-6 py-16 md:py-24">
        <h2 className="mb-10 text-center font-mono text-xl font-bold tracking-tight text-foreground dark:text-white md:text-2xl">
          Frequently asked questions
        </h2>
        <Accordion type="single" collapsible className="space-y-4">
          {data.faq.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="rounded-2xl border border-border bg-card dark:border-indigo-500/20 dark:bg-[#0e0c30]/80 px-5 backdrop-blur-sm"
            >
              <AccordionTrigger className="py-5 text-left font-mono text-base font-bold text-foreground dark:text-white hover:no-underline">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-base leading-relaxed text-foreground dark:text-indigo-100/90">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function CtaSection({ data }: { data: ComparisonData }) {
  return (
    <section className="relative border-t border-border dark:border-indigo-500/20 bg-background dark:bg-[#0c0a2a]">
      <div className="mx-auto max-w-3xl px-6 py-16 text-center md:py-24">
        <h2 className="mb-4 font-mono text-2xl font-bold tracking-tight text-foreground dark:text-white md:text-3xl">
          {data.cta.heading}
        </h2>
        <p className="mb-8 text-base text-muted-foreground dark:text-indigo-200/70">
          {data.cta.description}
        </p>
        <Link to={data.cta.buttonUrl}>
          <Button className="gap-2 rounded-full bg-gradient-to-r from-primary to-purple-500 px-10 text-base text-white shadow-[0_0_30px_rgba(99,102,241,0.25)] dark:shadow-[0_0_30px_rgba(99,102,241,0.4)] hover:from-primary/90 hover:to-purple-500/90">
            {data.cta.buttonText} <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
        <p className="mt-4 text-sm text-primary dark:text-indigo-300/50">
          No credit card required. Free trial included.
        </p>
      </div>
    </section>
  );
}

function ComparisonFooter() {
  return (
    <footer className="border-t border-border dark:border-indigo-500/20 bg-muted/40 dark:bg-[#0a0825]">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-6">
        <div className="flex flex-wrap items-center gap-4 text-sm text-primary dark:text-indigo-300/70">
          <div className="flex items-center gap-2">
            <Moon className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-300" />
            <span className="font-mono">PassiveAgents</span>
          </div>
          <Link
            to="/pricing"
            className="text-xs text-primary dark:text-indigo-300/50 hover:text-primary dark:hover:text-indigo-200"
          >
            Pricing
          </Link>
          <Link
            to="/docs?tab=setup"
            className="text-xs text-primary dark:text-indigo-300/50 hover:text-primary dark:hover:text-indigo-200"
          >
            Docs
          </Link>
          <a
            href="/privacyPolicy.html"
            className="text-xs text-primary dark:text-indigo-300/50 hover:text-primary dark:hover:text-indigo-200"
          >
            Privacy Policy
          </a>
          <a
            href="/termsOfService.html"
            className="text-xs text-primary dark:text-indigo-300/50 hover:text-primary dark:hover:text-indigo-200"
          >
            Terms
          </a>
        </div>
        <span className="text-xs text-primary dark:text-indigo-300/50">
          &copy; 2026 PassiveAgents. All rights reserved.
        </span>
      </div>
    </footer>
  );
}

function Breadcrumbs({ data }: { data: ComparisonData }) {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.passiveagents.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: `PassiveAgents vs ${data.competitor.name}`,
      },
    ],
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
    />
  );
}

export default function ComparisonPage({ data }: { data: ComparisonData }) {
  return (
    <div className="min-h-screen bg-background text-foreground dark:bg-[#0c0a2a]">
      <SeoHead data={data} />
      <FaqSchema data={data} />
      <Breadcrumbs data={data} />

      <section className="relative overflow-hidden border-b border-border dark:border-indigo-500/20">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-background to-background dark:from-[#1a1050] dark:via-[#0c0a2a] dark:to-[#0c0a2a]" />
        <div className="relative z-10 mx-auto max-w-4xl px-6 py-16 md:py-24">
          <div className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 dark:border-indigo-400/30 dark:bg-indigo-500/10 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-wider text-primary dark:text-indigo-300">
            Comparison
          </div>
          <h1 className="font-mono text-3xl font-bold leading-tight tracking-tight text-foreground dark:text-white md:text-5xl lg:text-6xl">
            PassiveAgents vs{" "}
            <span className="bg-gradient-to-r from-yellow-300 via-amber-300 to-orange-300 bg-clip-text text-transparent">
              {data.competitor.name}
            </span>
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground dark:text-indigo-200/80 md:text-lg">
            {data.shortAnswer}
          </p>
          <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="gap-2 rounded-full bg-gradient-to-r from-primary to-purple-500 text-white shadow-[0_0_30px_rgba(99,102,241,0.25)] dark:shadow-[0_0_30px_rgba(99,102,241,0.4)] hover:from-primary/90 hover:to-purple-500/90"
            >
              <Link to="/docs?tab=setup">
                Get started for free <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary/40 text-primary dark:border-indigo-400/40 dark:text-indigo-200 hover:bg-primary/10 dark:hover:bg-indigo-500/10"
            >
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </section>

      <ComparisonTable data={data} />
      <BestForSection data={data} />
      <KeyDifferencesSection data={data} />
      <WhenToUseSection data={data} />
      <CanYouUseBothSection data={data} />
      <FaqSection data={data} />
      <CtaSection data={data} />
      <ComparisonFooter />
    </div>
  );
}
